import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import "./Home.css";
import ViewSolicitacaoModal from './ViewSolicitacaoModal';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';
import { Grid, TextField } from '@mui/material';

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Sempre pega do localStorage
  const idPrefeitura = localStorage.getItem('idPrefeitura');
  const nomePrefeitura = localStorage.getItem('nomePrefeitura');

  // Se vier via state (ex: do login), salva no localStorage
  useEffect(() => {
    if (location.state?.idPrefeitura) {
      localStorage.setItem('idPrefeitura', location.state.idPrefeitura);
    }
    if (location.state?.nomePrefeitura) {
      localStorage.setItem('nomePrefeitura', location.state.nomePrefeitura);
    }
  }, [location.state]);

  useEffect(() => {
    const tipoUsuario = localStorage.getItem('tipoUsuario');
    if (!idPrefeitura || tipoUsuario !== 'PREFEITURA') {
      //console.log('Redirecionando para login - Tipo:', tipoUsuario, 'ID Prefeitura:', idPrefeitura);
      navigate('/', { replace: true });
    }
  }, [idPrefeitura, navigate]);

  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [paginacao, setPaginacao] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [solicitacoesFiltradas, setSolicitacoesFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Função para buscar solicitações da prefeitura via API 
  const buscarSolicitacoes = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 8
      };

      // Adiciona filtros de data apenas se estiverem preenchidos
      if (dataInicio) {
        params.data_inicio = dataInicio;
      }
      if (dataFim) {
        params.data_fim = dataFim;
      }

      const response = await api.get(API_CONFIG.ENDPOINTS.SOLICITACOES.PREFEITURA(idPrefeitura), {
        params
      });
      const data = response.data;
      if (data && data.status && data.solicitacoes) {
        setPaginacao(data.solicitacoes);
        // O estado solicitacoesFiltradas será atualizado pelo useEffect do filtro
      } else {
        setPaginacao(null);
      }
    } catch (error) {
     // console.error('Erro ao buscar solicitações:', error);
      setPaginacao(null);
    } finally {
      setLoading(false);
    }
  };

  // Função para limitar o tamanho do texto
  const limitarTexto = (texto, limite = 50) => {
    if (texto.length <= limite) return texto;
    return texto.substring(0, limite) + '...';
  };

  // Função para extrair o ID da solicitação do termo de busca
  const extrairIdSolicitacao = (termo) => {
    // Remove espaços e caracteres especiais, mantendo apenas números
    const id = termo.replace(/[^\d]/g, '');
    return id ? parseInt(id) : null;
  };

  // Função para formatar a data para comparação
  const formatarDataParaComparacao = (dataString) => {
    // Remove a hora da string de data
    const dataSemHora = dataString.split(' ')[0];
    return new Date(dataSemHora);
  };

  // Função para filtrar as solicitações
  const filtrarSolicitacoes = () => {
    let resultado = paginacao?.data ? [...paginacao.data] : []; 

    // Filtro por ID
    if (searchTerm) {
      const idSolicitacao = extrairIdSolicitacao(searchTerm);
      if (idSolicitacao !== null) { 
        resultado = resultado.filter(solicitacao =>
          solicitacao.SOL_ID === idSolicitacao
        );
      } else if (searchTerm.trim() !== '') { // Se houver texto mas não for um ID válido, não mostra nada
        resultado = [];
      }
    }

 

    setSolicitacoesFiltradas(resultado);
    
  };

  // Efeito para aplicar o filtro por searchTerm quando ele mudar ou quando os dados da paginação mudarem
  useEffect(() => {
    filtrarSolicitacoes();
  }, [searchTerm, paginacao?.data]); // Depende de searchTerm e paginacao.data

  // Buscar solicitações da prefeitura via API 
  useEffect(() => {
    if (idPrefeitura) {
      buscarSolicitacoes();
    }
  }, [idPrefeitura, currentPage, dataInicio, dataFim]); // Depende também dos filtros de data

  // Funções de navegação
  const goToPage = (page) => {
    if (page && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const nextPage = () => {
    if (paginacao?.next_page_url) {
      const url = new URL(paginacao.next_page_url);
      const page = url.searchParams.get('page');
      setCurrentPage(Number(page));
    }
  };

  const prevPage = () => {
    if (paginacao?.prev_page_url) {
      const url = new URL(paginacao.prev_page_url);
      const page = url.searchParams.get('page');
      setCurrentPage(Number(page));
    }
  };

  // Monitora mudanças no menu através de um evento personalizado
  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };

    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  const handleViewSolicitacao = (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSolicitacao(null);
  };

  return (
    <div className={`home-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      {/* Central Modal */}
      <div className="modal-wrapper">
        <h1 className="modal-title">Solicitações da Prefeitura</h1>
        <div className="modal-content">
          <div className="search-container" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="text"
              placeholder="Digite o Nº da solicitação (ex: 2)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{ minWidth: 180, maxWidth: 220, width: 180 }}
            />
            <TextField
              label="Data Início"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              inputProps={{ max: dataFim }}
              style={{ minWidth: 150 }}
            />
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              inputProps={{ min: dataInicio }}
              style={{ minWidth: 150 }}
            />
            <button
              className="search-button"
              onClick={buscarSolicitacoes} // Este botão agora dispara a busca na API (com datas)
              style={{ height: 40, marginLeft: 4 }}
            >
              PROCURAR
            </button>
          </div>

          <div className="cards-grid">
            {loading ? (
              <div className="loading">
                Carregando solicitações...
              </div>
            ) : solicitacoesFiltradas.length > 0 ? ( // Renderiza a partir de solicitacoesFiltradas
              solicitacoesFiltradas.map((solicitacao) => (
                <div key={solicitacao.SOL_ID} className="card">
                  <h2 className="card-title">Solicitação #{solicitacao.SOL_ID}</h2>
                  <div className="card-content">
                    <div className="card-info">
                      <strong>Veículo:</strong> {solicitacao.VEI_DESCRICAO} ({solicitacao.VEI_PLACA})
                    </div>
                    <div className="card-info">
                      <strong>Quantidade de Itens:</strong> {solicitacao.quantidade_itens}
                    </div>
                    <div className="card-info">
                      <strong>Status:</strong> {solicitacao.SOL_STATUS}
                    </div>
                    <div className="card-info">
                      <strong>Data de abertura:</strong> {new Date(solicitacao.SOL_DATA_VINCULACAO).toLocaleDateString('pt-BR')}
                    </div>
                    <div className="card-info">
                      <strong>Tipo:</strong> {solicitacao.VEI_TIPO}
                    </div>
                    <button
                      className="ver-cotacao-btn"
                      onClick={() => handleViewSolicitacao(solicitacao)}
                    >
                      Ver Detalhes
                    </button>
                  </div>
                </div>
              ))
            ) : ( paginacao && paginacao.data && paginacao.data.length > 0 && searchTerm ) ? (
                 // Se houver dados paginados, mas nenhum resultado após o filtro local
                 <div className="no-results">
                   Nenhuma solicitação encontrada com o número informado nesta página.
                 </div>
            ) : (
              <div className="no-results">
                Nenhuma solicitação encontrada para os filtros aplicados.
              </div>
            )}
          </div>

          <div className="pagination">
            {paginacao && (
              <>
                {paginacao.prev_page_url && (
                  <button className="pagination-btn" onClick={prevPage} disabled={loading}>
                    Voltar
                  </button>
                )}

                <span className="pagination-info">
                  Página {paginacao.current_page} de {paginacao.last_page}
                </span>
                {paginacao.next_page_url && (
                  <button className="pagination-btn" onClick={nextPage} disabled={loading}>
                    Próximo
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ViewSolicitacaoModal
        isVisible={isModalOpen}
        onClose={handleCloseModal}
        solicitacao={selectedSolicitacao}
      />
    </div>
  );
};

export default Home;